import { MappingSystem } from '@atlas/types'
import { Field, SectionCard } from '@atlas/ui-components'
import { Button, Tab, TabList, TabPanel, Tabs } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
	useAxis,
	useCreateMapping,
	useMappings,
	useRemoveMarketName,
	useReparentValue,
	useUpdateValue,
	useUpdateValueMeta,
	useValue,
	useValues,
} from '@/hooks/useClassification'
import { MarketNames } from '@/pages/Explore/MarketNames'
import { MetaJsonEditor } from '@/pages/Explore/MetaJsonEditor'
import { AvailabilityEditor } from './AvailabilityPage'
import { LabelledCombobox } from './FormField'

/**
 * One axis value, in four tabs — the same four for every value on every axis.
 *
 * The tabs exist because a value is four separate things at once, and a single scrolling
 * form made them compete: what it IS, what it is CALLED per market, WHERE it applies, and
 * what it is known as in other systems. Before this, the last two were pages reachable
 * only by typing a URL, and meta was editable nowhere.
 *
 * Identical across axes on purpose. A geography value and a licence value differ in their
 * data, not in what an administrator can do to them, so learning one teaches all thirty.
 */

const DETAILS_TAB = 'details'
const NAMES_TAB = 'names'
const AVAILABILITY_TAB = 'availability'
const CODES_TAB = 'codes'

/** The value's own fields. `code` is immutable — every classification row points at it. */
function DetailsTab({ axisId, valueId }: { axisId: string; valueId: string }) {
	const { value } = useValue(axisId, valueId)
	const { data: siblings = [] } = useValues(axisId)
	const { axis } = useAxis(axisId)
	const update = useUpdateValue(axisId)
	const reparent = useReparentValue(axisId)
	const [form, setForm] = useState({ label: '', status: 'active', parentId: '' })

	useEffect(() => {
		if (!value) return
		setForm({ label: value.label, status: value.status, parentId: value.parentId ?? '' })
	}, [value])

	if (!value) return null
	const parentChanged = (value.parentId ?? '') !== form.parentId
	const fieldsChanged = form.label !== value.label || form.status !== value.status

	const save = () => {
		if (fieldsChanged) update.mutate({ valueId, body: { label: form.label.trim(), status: form.status } })
		// Re-parenting is its own endpoint: it rebuilds the closure, which a label edit
		// must not trigger.
		if (parentChanged) reparent.mutate({ valueId, parentId: form.parentId || null })
	}

	return (
		<SectionCard title="Details" description="What this value is. Its code is permanent.">
			<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
				<Field label="Code" value={value.code} readOnly description="Immutable — every tag points at it." />
				<Field label="Label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
				<LabelledCombobox
					label="Status"
					value={form.status}
					onChange={(status) => setForm({ ...form, status: status ?? form.status })}
					options={[
						{ value: 'active', label: 'active' },
						{ value: 'retired', label: 'retired' },
					]}
				/>
				{axis?.isHierarchical && (
					<LabelledCombobox
						label="Parent"
						value={form.parentId}
						onChange={(parentId) => setForm({ ...form, parentId: parentId ?? '' })}
						// No parent is a real choice — the value is a root — so it is named.
						placeholder="(root)"
						options={siblings.filter((v) => v.valueId !== valueId).map((v) => ({ value: v.valueId, label: v.label }))}
					/>
				)}
			</div>
			<div className="mt-4 flex justify-end">
				<Button
					type="button"
					className="rounded-ds-full px-5"
					isDisabled={(!fieldsChanged && !parentChanged) || update.isPending || !form.label.trim()}
					onPress={save}
				>
					{update.isPending || reparent.isPending ? 'Saving…' : 'Save'}
				</Button>
			</div>
		</SectionCard>
	)
}

const SYSTEM_LABEL: Record<string, string> = {
	[MappingSystem.VEEVA]: 'Veeva',
	[MappingSystem.WORKFRONT]: 'Workfront',
}

/** What this value is called in the systems ATLAS exchanges with. */
function ExternalCodes({ valueId }: { valueId: string }) {
	const { data: mappings = [] } = useMappings(valueId)
	const createMapping = useCreateMapping(valueId)
	const removeName = useRemoveMarketName(valueId)
	const [form, setForm] = useState({ system: MappingSystem.VEEVA as string, externalCode: '' })

	// Market names have their own tab; these are the worldwide codes for other systems.
	const rows = mappings.filter((m) => m.system !== MappingSystem.TAXONOMY_TITLE)

	return (
		<SectionCard title="External codes" description="What this value is called in Veeva and Workfront.">
			{rows.length === 0 ? (
				<p className="text-ds-text-muted text-sm">No external code is recorded for this value.</p>
			) : (
				rows.map((row) => (
					<div
						key={`${row.system}:${row.geoValueId ?? 'worldwide'}`}
						className="flex items-baseline justify-between gap-4 border-ds-border border-b py-2 last:border-0"
					>
						<span className="text-ds-text-muted text-xs">{SYSTEM_LABEL[row.system] ?? row.system}</span>
						<span className="flex items-center gap-3">
							<span className="font-mono text-ds-text text-sm">{row.externalCode}</span>
							{row.geoValueId && <span className="text-ds-text-subtle text-xs">scoped</span>}
							<button
								type="button"
								className="text-ds-danger text-xs hover:underline"
								onClick={() => removeName.mutate({ geoValueId: row.geoValueId })}
							>
								remove
							</button>
						</span>
					</div>
				))
			)}

			<div className="mt-3 flex flex-wrap items-end gap-2">
				<LabelledCombobox
					label="System"
					value={form.system}
					onChange={(system) => setForm({ ...form, system: system ?? form.system })}
					options={[
						{ value: MappingSystem.VEEVA, label: 'Veeva' },
						{ value: MappingSystem.WORKFRONT, label: 'Workfront' },
					]}
				/>
				<Field
					label="External code"
					value={form.externalCode}
					onChange={(e) => setForm({ ...form, externalCode: e.target.value })}
					className="min-w-60 flex-1"
				/>
				<Button
					type="button"
					className="rounded-ds-full"
					isDisabled={createMapping.isPending || !form.externalCode.trim()}
					onPress={() =>
						createMapping.mutate(
							{ system: form.system, externalCode: form.externalCode.trim() },
							{ onSuccess: () => setForm({ ...form, externalCode: '' }) },
						)
					}
				>
					{createMapping.isPending ? 'Saving…' : 'Add code'}
				</Button>
			</div>
		</SectionCard>
	)
}

export function ValueDetailPage() {
	const { axisId = '', valueId = '' } = useParams()
	const { axis } = useAxis(axisId)
	const { value } = useValue(axisId, valueId)
	const { data: mappings = [] } = useMappings(valueId)
	const updateMeta = useUpdateValueMeta(axisId, valueId)

	if (!value) {
		return (
			<div className="space-y-4">
				<Link to={`/admin/classification/axes/${axisId}`} className="text-ds-info text-sm hover:underline">
					← Back to the axis
				</Link>
				<p className="text-ds-text-muted">This value is not in the registry.</p>
			</div>
		)
	}

	return (
		<div className="space-y-6">
			<div>
				<Link to={`/admin/classification/axes/${axisId}`} className="text-ds-info text-sm hover:underline">
					← {axis?.name ?? 'Axis'}
				</Link>
				<h1 className="mt-1 font-semibold text-2xl">{value.label}</h1>
				<p className="text-ds-text-muted">
					<span className="font-mono text-sm">{value.code}</span>
					{axis ? ` · ${axis.name}` : ''}
					{value.status === 'retired' ? ' · retired' : ''}
				</p>
			</div>

			<Tabs defaultSelectedKey={DETAILS_TAB}>
				<TabList variant="underline" className="mb-4 flex w-full" aria-label="Value sections">
					<Tab id={DETAILS_TAB}>Details</Tab>
					<Tab id={NAMES_TAB}>Names</Tab>
					<Tab id={AVAILABILITY_TAB}>Availability</Tab>
					<Tab id={CODES_TAB}>Meta &amp; codes</Tab>
				</TabList>

				<TabPanel id={DETAILS_TAB}>
					<DetailsTab axisId={axisId} valueId={valueId} />
				</TabPanel>

				<TabPanel id={NAMES_TAB}>
					<MarketNames
						valueId={valueId}
						subject={axis?.name.toLowerCase() ?? 'value'}
						defaultName={value.label}
						mappings={mappings}
					/>
				</TabPanel>

				<TabPanel id={AVAILABILITY_TAB}>
					<AvailabilityEditor valueId={valueId} />
				</TabPanel>

				<TabPanel id={CODES_TAB} className="space-y-4">
					<ExternalCodes valueId={valueId} />
					<MetaJsonEditor
						meta={value.meta ?? {}}
						subject={axis?.name.toLowerCase() ?? 'value'}
						title="Raw meta"
						description="Everything stored on this value that the fields above do not cover."
						saving={updateMeta.isPending}
						onSave={(meta) => updateMeta.mutate(meta)}
					/>
				</TabPanel>
			</Tabs>
		</div>
	)
}
